import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import classNames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';
import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect } from 'react';
import { PropTypes as PropTypes$1 } from '@panneau/core';
import { useLocales, useFieldsComponents } from '@panneau/core/contexts';
import { getComponentFromName } from '@panneau/core/utils';
import Buttons from '@panneau/element-buttons';
import FormGroup from '@panneau/element-form-group';
import Label from '@panneau/element-label';

var _excluded = ["name", "component"];
var propTypes = {
  name: PropTypes.string,
  value: PropTypes.object,
  // eslint-disable-line
  errors: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  label: PropTypes.string,
  helpText: PropTypes.string,
  component: PropTypes.string,
  locales: PropTypes.arrayOf(PropTypes.string),
  properties: PropTypes.objectOf(PropTypes$1.field),
  fieldComponent: PropTypes.elementType,
  fieldProps: PropTypes.object,
  // eslint-disable-line
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  onCurrentLocaleChange: PropTypes.func
};
var defaultProps = {
  name: null,
  value: null,
  errors: null,
  label: null,
  helpText: null,
  component: null,
  locales: null,
  properties: {},
  fieldComponent: null,
  fieldProps: null,
  disabled: false,
  className: null,
  onChange: null,
  onCurrentLocaleChange: null
};
var LocalizedField = function LocalizedField(_ref) {
  var name = _ref.name,
    value = _ref.value,
    errors = _ref.errors,
    label = _ref.label,
    helpText = _ref.helpText,
    properties = _ref.properties,
    parentLocales = _ref.locales,
    providedFieldComponent = _ref.fieldComponent,
    componentName = _ref.component,
    fieldProps = _ref.fieldProps,
    onChange = _ref.onChange,
    onCurrentLocaleChange = _ref.onCurrentLocaleChange,
    disabled = _ref.disabled,
    className = _ref.className;
  var contextLocales = useLocales();
  var locales = parentLocales || contextLocales;
  var Components = useFieldsComponents();
  var onFieldChange = useCallback(function (locale, newFieldValue) {
    var newValue = _objectSpread(_objectSpread({}, value), {}, _defineProperty({}, locale, newFieldValue));
    if (onChange !== null) {
      onChange(newValue);
    }
  }, [onChange, value]);
  var _useState = useState(locales.length > 0 ? locales[0] : null),
    _useState2 = _slicedToArray(_useState, 2),
    currentLocale = _useState2[0],
    setCurrentLocale = _useState2[1];
  useEffect(function () {
    if (onCurrentLocaleChange !== null) {
      onCurrentLocaleChange(currentLocale);
    }
  }, [currentLocale, onCurrentLocaleChange]);
  return /*#__PURE__*/React.createElement(FormGroup, {
    label: /*#__PURE__*/React.createElement(React.Fragment, null, label !== null ? /*#__PURE__*/React.createElement("span", {
      className: "me-2"
    }, /*#__PURE__*/React.createElement(Label, null, label)) : null, locales.length > 1 ? /*#__PURE__*/React.createElement("div", {
      className: "ms-auto"
    }, /*#__PURE__*/React.createElement(Buttons, {
      items: locales.map(function (locale) {
        var _ref2 = properties[locale] || {},
          _ref2$label = _ref2.label,
          localeLabel = _ref2$label === void 0 ? null : _ref2$label;
        return {
          id: locale,
          label: localeLabel || locale.toUpperCase(),
          active: locale === currentLocale,
          underlined: locale === currentLocale,
          theme: value !== null && !isEmpty(value[locale] || null) ? 'success' : 'warning',
          onClick: function onClick() {
            return setCurrentLocale(locale);
          }
        };
      }),
      theme: "secondary",
      size: "sm",
      outline: true
    })) : null),
    helpText: helpText,
    className: classNames(['mb-3', className]),
    errors: errors,
    labelClassName: "d-flex align-items-center"
  }, locales.filter(function (locale) {
    return locale === currentLocale;
  }).map(function (locale) {
    var _ref3 = properties[locale] || {},
      _ref3$name = _ref3.name,
      propertyName = _ref3$name === void 0 ? locale : _ref3$name,
      component = _ref3.component,
      property = _objectWithoutProperties(_ref3, _excluded);
    var FieldComponent = providedFieldComponent || getComponentFromName(component || componentName, Components);
    var fieldName = "".concat(name, "[").concat(componentName, "]");
    var fieldValue = value !== null ? value[locale] || null : null;
    return /*#__PURE__*/React.createElement("div", {
      key: "field-".concat(locale)
    }, /*#__PURE__*/React.createElement(FieldComponent, Object.assign({
      fieldLocale: locale
    }, disabled === true ? {
      disabled: disabled
    } : null, property, fieldProps, {
      name: propertyName || fieldName,
      value: fieldValue,
      errors: errors,
      onChange: function onChange(newValue) {
        return onFieldChange(locale, newValue);
      }
    })));
  }));
};
LocalizedField.propTypes = propTypes;
LocalizedField.defaultProps = defaultProps;

var definition = {
  id: 'localized',
  component: 'Localized'
};

export { LocalizedField as default, definition };
